// What this credential may do, read off `/v1/me`'s published `scopes` (#7426).
//
// `Me.scopes` (`lib/session.ts`) is the AUTHORITY the API publishes ahead of the
// refusal — `role` does not answer it (a PAT carries none, a cap narrows a
// role's ceiling), so this never re-derives a scope from the role table. A
// surface asks one question per route scope, and the answer is THREE-valued:
//
//   - `allowed`   — mount the fetch / show the control. Also the answer for a
//                   settled `/v1/me` that publishes NO list (legacy answer,
//                   partial mock): the server stays the arbiter, so absence
//                   fails open, `filterRailItemsByScope`'s rule.
//   - `denied`    — the settled list lacks it: send nothing, and say why.
//   - `unsettled` — `/v1/me` has not answered for THIS tenant (`roleSettled`):
//                   no fetch yet, and no "you can't" line that flips a moment
//                   later.
//
// A role-gated route (`requireOwner` on `/v1/org-sso`) has no scope to read; it
// stays on `lib/roles.ts`.

import { roleSettled } from './roles';
import { SESSION_TOKEN, useMe } from './session';

export type ScopeVerdict = 'allowed' | 'denied' | 'unsettled';

/** The `useMe` fields the verdict reads — structural, so a test needs no QueryClient. */
export type MeScopesRead = {
  readonly isSuccess: boolean;
  readonly isPlaceholderData: boolean;
  readonly data?: { readonly scopes?: readonly string[] | undefined } | undefined;
};

/** May this credential use `scope`, per its settled `/v1/me`? */
export function scopeVerdict(me: MeScopesRead, scope: string): ScopeVerdict {
  if (!roleSettled(me)) return 'unsettled';
  const scopes = me.data?.scopes;
  return scopes === undefined || scopes.includes(scope) ? 'allowed' : 'denied';
}

/** {@link scopeVerdict} as a reactive accessor over this tenant's `/v1/me`. */
export function useScopeVerdict(
  scope: string,
  token: string | null = SESSION_TOKEN,
): () => ScopeVerdict {
  const me = useMe(token);
  return () => scopeVerdict(me, scope);
}
